/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useCallback } from 'react';
import type { Agent } from '@shared/schema/agents';
import type {
  SystemAgentRun,
  SystemAgentFinding,
  SystemAgentSchedule,
  SystemAgentMetadata,
  FindingSeverity,
  FindingStatus,
} from '@shared/schema/systemAgents';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { AgentFocusTabMenu, type AgentFocusTab } from './AgentFocusTabMenu';
import { ScheduleSelector, cronToHumanReadable } from './ScheduleSelector';
import {
  listSystemAgentRuns,
  listSystemAgentFindings,
  listSystemAgentSchedules,
  triggerSystemAgentRun,
  updateSystemAgentFinding,
  createSystemAgentSchedule,
  updateSystemAgentSchedule,
  deleteSystemAgentSchedule,
  updateSystemAgent,
  listLLMModels,
  getSeverityColor,
  getRunStatusColor,
  getFindingStatusColor,
  formatDuration,
  formatRelativeTime,
  getAgentCategoryLabel,
} from '../../lib/api/system-agents';
import type { LLMModel } from '../../lib/api/system-agents';
import {
  Play,
  Pause,
  Shield,
  BarChart3,
  GitBranch,
  FileText,
  Bot,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Clock,
  Calendar,
  Loader2,
  RefreshCw,
  ChevronLeft,
  ExternalLink,
  FileCode,
  Trash2,
  Eye,
  EyeOff,
  Plus,
  Save,
  Edit,
  X,
} from 'lucide-react';

interface AgentFocusViewProps {
  agent: Agent;
  /** Active product ID for scoping findings to product-specific storage */
  activeProductId?: string;
  onBack: () => void;
  onRunTriggered?: () => void;
}

export function AgentFocusView({ agent, activeProductId, onBack, onRunTriggered }: AgentFocusViewProps) {
  const [activeTab, setActiveTab] = useState<AgentFocusTab>('overview');
  const [runs, setRuns] = useState<SystemAgentRun[]>([]);
  const [findings, setFindings] = useState<SystemAgentFinding[]>([]);
  const [schedules, setSchedules] = useState<SystemAgentSchedule[]>([]);
  const [isLoadingRuns, setIsLoadingRuns] = useState(false);
  const [isLoadingFindings, setIsLoadingFindings] = useState(false);
  const [isLoadingSchedules, setIsLoadingSchedules] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [newCron, setNewCron] = useState('0 9 * * *');
  const [isCreatingSchedule, setIsCreatingSchedule] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState('');
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);
  const [isEditingModel, setIsEditingModel] = useState(false);
  const [selectedModel, setSelectedModel] = useState('');
  const [isSavingModel, setIsSavingModel] = useState(false);
  const [availableModels, setAvailableModels] = useState<LLMModel[]>([]);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const { toast } = useToast();

  // Computed values
  const metadata = agent.metadata as SystemAgentMetadata | undefined;
  const openFindingsCount = findings.filter(f => f.status === 'open').length;

  // Fetch runs for this agent
  const fetchRuns = useCallback(async () => {
    setIsLoadingRuns(true);
    try {
      const result = await listSystemAgentRuns({ agentId: agent.id, limit: 20 });
      if (result.success && result.runs) {
        setRuns(result.runs);
        // Check if any run is currently running
        const hasRunning = result.runs.some((r: SystemAgentRun) => r.status === 'running' || r.status === 'pending');
        setIsRunning(hasRunning);
      }
    } catch (error) {
      console.error('Error fetching runs:', error);
    } finally {
      setIsLoadingRuns(false);
    }
  }, [agent.id]);

  // Fetch findings for this agent
  // If activeProductId is provided, reads from product-specific VPS path
  // Otherwise reads from org-wide DSA findings
  const fetchFindings = useCallback(async () => {
    setIsLoadingFindings(true);
    try {
      const result = await listSystemAgentFindings({ 
        agentId: agent.id, 
        productId: activeProductId,
        limit: 50 
      });
      if (result.success && result.findings) {
        setFindings(result.findings);
      }
    } catch (error) {
      console.error('Error fetching findings:', error);
    } finally {
      setIsLoadingFindings(false);
    }
  }, [agent.id, activeProductId]);

  // Fetch schedules for this agent
  const fetchSchedules = useCallback(async () => {
    setIsLoadingSchedules(true);
    try {
      const result = await listSystemAgentSchedules(agent.id);
      if (result.success && result.schedules) {
        setSchedules(result.schedules);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    } finally {
      setIsLoadingSchedules(false);
    }
  }, [agent.id]);

  // Load data on mount and tab change
  useEffect(() => {
    fetchRuns();
    fetchFindings();
    fetchSchedules();
  }, [fetchRuns, fetchFindings, fetchSchedules]);

  // Fetch available LLM models for the model selector
  const fetchModels = useCallback(async () => {
    setIsLoadingModels(true);
    try {
      const result = await listLLMModels();
      if (result.success && result.models) {
        setAvailableModels(result.models);
      }
    } catch (error) {
      console.error('Error fetching LLM models:', error);
    } finally {
      setIsLoadingModels(false);
    }
  }, []);

  // Load models when entering edit mode
  useEffect(() => {
    if (isEditingModel && availableModels.length === 0) {
      fetchModels();
    }
  }, [isEditingModel, availableModels.length, fetchModels]);

  // Handle triggering a new run
  const handleRunNow = async () => {
    setIsRunning(true);
    try {
      const result = await triggerSystemAgentRun(agent.id);
      if (result.success) {
        toast({
          title: 'Agent run started',
          description: `${agent.displayName || agent.name} is now running.`,
        });
        onRunTriggered?.();
        // Refresh runs
        setTimeout(fetchRuns, 1000);
      } else {
        toast({
          title: 'Failed to start run',
          description: result.error || 'An error occurred.',
          variant: 'destructive',
        });
        setIsRunning(false);
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to trigger agent run.',
        variant: 'destructive',
      });
      setIsRunning(false);
    }
  };

  // Handle updating finding status
  const handleUpdateFindingStatus = async (findingId: string, status: FindingStatus, reason?: string) => {
    try {
      const result = await updateSystemAgentFinding(findingId, {
        status,
        dismissedReason: reason,
      }, activeProductId);
      if (result.success) {
        toast({
          title: 'Finding updated',
          description: `Finding status changed to ${status}.`,
        });
        fetchFindings();
      } else {
        toast({
          title: 'Failed to update finding',
          description: result.error,
          variant: 'destructive',
        });
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  // Get icon based on agent category
  const getIcon = () => {
    switch (agent.category) {
      case 'system_audit':
        return <Shield className="h-6 w-6 text-red-500" />;
      case 'system_measurement':
        return <BarChart3 className="h-6 w-6 text-blue-500" />;
      case 'system_review':
        return <GitBranch className="h-6 w-6 text-purple-500" />;
      case 'system_evaluation':
        return <FileText className="h-6 w-6 text-green-500" />;
      default:
        return <Bot className="h-6 w-6 text-gray-500" />;
    }
  };

  // Render Overview Tab
  const renderOverviewTab = () => {
    const lastRun = runs[0];
    
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Main Info Card */}
          <Card className="md:col-span-2">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted">
                  {getIcon()}
                </div>
                <div>
                  <CardTitle>{agent.displayName || agent.name}</CardTitle>
                  <CardDescription className="mt-1">
                    {getAgentCategoryLabel(agent.category)}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Description</h4>
                <p className="text-sm text-muted-foreground">{agent.description}</p>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-2">Status</h4>
                  <Badge variant={agent.status === 'active' ? 'default' : 'secondary'}>
                    {agent.status === 'active' ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Target Products</h4>
                  <span className="text-sm text-muted-foreground">
                    {metadata?.targetProducts === 'all' 
                      ? 'All products' 
                      : metadata?.targetProducts === 'active'
                        ? 'Active products only'
                        : Array.isArray(metadata?.targetProducts)
                          ? `${metadata.targetProducts.length} selected`
                          : 'All products'}
                  </span>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-medium mb-2">Tools Used</h4>
                <div className="flex flex-wrap gap-2">
                  {(agent.enabledTools || []).map((tool) => (
                    <Badge key={tool} variant="outline" className="text-xs">
                      {tool}
                    </Badge>
                  ))}
                  {(!agent.enabledTools || agent.enabledTools.length === 0) && (
                    <span className="text-sm text-muted-foreground">No tools configured</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Statistics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Last Run</span>
                <span className="text-sm font-medium">
                  {lastRun ? formatRelativeTime(lastRun.createdAt!) : 'Never'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Total Runs</span>
                <span className="text-sm font-medium">{runs.length}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Open Findings</span>
                <span className="text-sm font-medium text-red-600">{openFindingsCount}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Schedule</span>
                <span className="text-sm font-medium">
                  {schedules.length > 0 ? cronToHumanReadable(schedules[0].cronExpression) : 'None'}
                </span>
              </div>

              <Separator />

              <Button 
                className="w-full gap-2" 
                onClick={handleRunNow}
                disabled={isRunning || agent.status === 'inactive'}
              >
                {isRunning ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4" />
                    Run Now
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingRuns ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : runs.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No runs yet. Click "Run Now" to start the first analysis.
              </p>
            ) : (
              <div className="space-y-3">
                {runs.slice(0, 5).map((run) => (
                  <div
                    key={run.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      {run.status === 'completed' && run.result === 'pass' && (
                        <CheckCircle2 className="h-5 w-5 text-green-500" />
                      )}
                      {run.status === 'completed' && run.result === 'warning' && (
                        <AlertTriangle className="h-5 w-5 text-yellow-500" />
                      )}
                      {run.status === 'completed' && run.result === 'fail' && (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      {run.status === 'running' && (
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />
                      )}
                      {run.status === 'failed' && (
                        <XCircle className="h-5 w-5 text-red-500" />
                      )}
                      {run.status === 'pending' && (
                        <Clock className="h-5 w-5 text-yellow-500" />
                      )}
                      <div>
                        <p className="text-sm font-medium">
                          {run.summary || `Run ${run.runId}`}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {formatRelativeTime(run.createdAt!)} • {run.triggeredBy}
                        </p>
                      </div>
                    </div>
                    <Badge className={getRunStatusColor(run.status)}>
                      {run.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Findings Tab
  const renderFindingsTab = () => {
    const severityOrder: FindingSeverity[] = ['critical', 'high', 'medium', 'low', 'info'];
    const sortedFindings = [...findings].sort((a, b) => {
      return severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity);
    });

    return (
      <div className="space-y-6">
        {/* Filters */}
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium">Filter:</span>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-muted"
              >
                All ({findings.length})
              </Badge>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-muted bg-red-50"
              >
                Open ({findings.filter(f => f.status === 'open').length})
              </Badge>
              <Badge 
                variant="outline" 
                className="cursor-pointer hover:bg-muted"
              >
                Fixed ({findings.filter(f => f.status === 'fixed').length})
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchFindings}
                className="ml-auto gap-1.5"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Findings List */}
        {isLoadingFindings ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : sortedFindings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
              <h3 className="text-lg font-medium mb-2">No Findings</h3>
              <p className="text-sm text-muted-foreground">
                No issues have been found by this agent. Great job!
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {sortedFindings.map((finding) => (
              <Card key={finding.findingId}>
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    {/* Severity Indicator */}
                    <Badge className={`${getSeverityColor(finding.severity)} shrink-0`}>
                      {finding.severity.toUpperCase()}
                    </Badge>

                    {/* Finding Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm mb-1">{finding.title}</h4>
                      {finding.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {finding.description}
                        </p>
                      )}
                      
                      {/* File Location */}
                      {finding.filePath && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                          <FileCode className="h-3 w-3" />
                          <code className="bg-muted px-1 py-0.5 rounded">
                            {finding.filePath}
                            {finding.lineNumber && `:${finding.lineNumber}`}
                          </code>
                        </div>
                      )}

                      {/* Code Snippet */}
                      {finding.codeSnippet && (
                        <pre className="text-xs bg-muted p-2 rounded overflow-x-auto mb-2">
                          {finding.codeSnippet}
                        </pre>
                      )}

                      {/* Recommendation */}
                      {finding.recommendation && (
                        <div className="text-sm bg-blue-50 border border-blue-200 rounded p-2 mb-2">
                          <span className="font-medium text-blue-800">Recommendation:</span>{' '}
                          <span className="text-blue-700">{finding.recommendation}</span>
                        </div>
                      )}

                      {/* Meta Info */}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Category: {finding.category}</span>
                        {finding.productId && <span>Product: {finding.productId}</span>}
                        <span>Found: {formatRelativeTime(finding.createdAt!)}</span>
                      </div>
                    </div>

                    {/* Status & Actions */}
                    <div className="flex flex-col items-end gap-2 shrink-0">
                      <Badge className={getFindingStatusColor(finding.status!)}>
                        {finding.status}
                      </Badge>
                      
                      {finding.status === 'open' && (
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateFindingStatus(finding.findingId, 'acknowledged')}
                          >
                            Acknowledge
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleUpdateFindingStatus(finding.findingId, 'dismissed', 'Not applicable')}
                          >
                            Dismiss
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render History Tab
  const renderHistoryTab = () => {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium">Run History</h3>
          <Button variant="outline" size="sm" onClick={fetchRuns} className="gap-1.5">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>

        {isLoadingRuns ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : runs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Clock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No Run History</h3>
              <p className="text-sm text-muted-foreground mb-4">
                This agent hasn't been run yet.
              </p>
              <Button onClick={handleRunNow} disabled={isRunning} className="gap-2">
                <Play className="h-4 w-4" />
                Run Now
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {runs.map((run) => (
              <Card key={run.id}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {run.status === 'completed' && (
                        <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                      )}
                      {run.status === 'running' && (
                        <Loader2 className="h-5 w-5 text-blue-500 animate-spin mt-0.5" />
                      )}
                      {run.status === 'failed' && (
                        <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
                      )}
                      {run.status === 'pending' && (
                        <Clock className="h-5 w-5 text-yellow-500 mt-0.5" />
                      )}
                      {run.status === 'cancelled' && (
                        <XCircle className="h-5 w-5 text-gray-500 mt-0.5" />
                      )}
                      
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{run.runId}</span>
                          <Badge className={getRunStatusColor(run.status)}>
                            {run.status}
                          </Badge>
                          {run.result && (
                            <Badge variant="outline">
                              {run.result}
                            </Badge>
                          )}
                        </div>
                        
                        {run.summary && (
                          <p className="text-sm text-muted-foreground mb-2">
                            {run.summary}
                          </p>
                        )}
                        
                        {run.errorMessage && (
                          <p className="text-sm text-red-600 mb-2">
                            Error: {run.errorMessage}
                          </p>
                        )}

                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Started: {run.startedAt ? formatRelativeTime(run.startedAt) : 'Pending'}</span>
                          {run.durationSeconds && (
                            <span>Duration: {formatDuration(run.durationSeconds)}</span>
                          )}
                          <span>Trigger: {run.triggeredBy}</span>
                          {run.productsAnalyzed !== undefined && (
                            <span>Products: {run.productsAnalyzed}</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-right text-xs text-muted-foreground">
                      {run.createdAt && new Date(run.createdAt).toLocaleString()}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Render Schedule Tab
  const renderScheduleTab = () => {
    const handleCreateSchedule = async () => {
      setIsCreatingSchedule(true);
      try {
        const result = await createSystemAgentSchedule({
          agentId: agent.id,
          cronExpression: newCron,
          timezone: 'UTC',
          enabled: true,
        });
        if (result.success) {
          toast({
            title: 'Schedule created',
            description: 'The agent will run according to the schedule.',
          });
          fetchSchedules();
          setNewCron('0 9 * * *');
        } else {
          toast({
            title: 'Failed to create schedule',
            description: result.error,
            variant: 'destructive',
          });
        }
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      } finally {
        setIsCreatingSchedule(false);
      }
    };

    const handleToggleSchedule = async (schedule: SystemAgentSchedule) => {
      try {
        const result = await updateSystemAgentSchedule(schedule.id, {
          enabled: !schedule.enabled,
        });
        if (result.success) {
          toast({
            title: schedule.enabled ? 'Schedule disabled' : 'Schedule enabled',
          });
          fetchSchedules();
        }
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      }
    };

    const handleDeleteSchedule = async (scheduleId: number) => {
      if (!confirm('Are you sure you want to delete this schedule?')) return;
      
      try {
        const result = await deleteSystemAgentSchedule(scheduleId);
        if (result.success) {
          toast({
            title: 'Schedule deleted',
          });
          fetchSchedules();
        }
      } catch (error: any) {
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive',
        });
      }
    };

    return (
      <div className="space-y-6">
        {/* Create Schedule */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Create Schedule</CardTitle>
            <CardDescription>
              Schedule automatic runs for this agent
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ScheduleSelector
              value={newCron}
              onChange={setNewCron}
            />
            <div className="flex justify-end pt-2">
              <Button 
                onClick={handleCreateSchedule}
                disabled={isCreatingSchedule || !newCron.trim()}
                className="gap-2"
              >
                {isCreatingSchedule ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Add Schedule
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Existing Schedules */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Active Schedules</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoadingSchedules ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : schedules.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No schedules configured. Create one above.
              </p>
            ) : (
              <div className="space-y-3">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/50"
                  >
                    <div className="flex items-center gap-3">
                      <Calendar className={`h-5 w-5 ${schedule.enabled ? 'text-green-500' : 'text-gray-400'}`} />
                      <div>
                        <span className="text-sm font-medium">{cronToHumanReadable(schedule.cronExpression)}</span>
                        <p className="text-xs text-muted-foreground">
                          {schedule.nextRunAt && `Next run: ${new Date(schedule.nextRunAt).toLocaleString()}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={schedule.enabled ? 'default' : 'secondary'}>
                        {schedule.enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleToggleSchedule(schedule)}
                      >
                        {schedule.enabled ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render Configure Tab
  const renderConfigureTab = () => {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Agent Configuration</CardTitle>
            <CardDescription>
              View and modify agent settings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Model */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">AI Model</label>
                <div className="flex gap-2">
                  {isEditingModel ? (
                    <>
                      <Button
                        size="sm"
                        onClick={async () => {
                          setIsSavingModel(true);
                          try {
                            const result = await updateSystemAgent(agent.id, {
                              defaultModel: selectedModel,
                            });
                            if (result.success) {
                              toast({
                                title: 'AI Model updated',
                                description: 'The agent model has been saved.',
                              });
                              setIsEditingModel(false);
                              // Trigger a refresh of the agent data
                              onRunTriggered?.();
                            } else {
                              toast({
                                title: 'Failed to save',
                                description: result.error || 'An error occurred.',
                                variant: 'destructive',
                              });
                            }
                          } catch (error: any) {
                            toast({
                              title: 'Error',
                              description: error.message || 'Failed to save AI model.',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsSavingModel(false);
                          }
                        }}
                        disabled={isSavingModel}
                        className="gap-1.5"
                      >
                        {isSavingModel ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Saving
                          </>
                        ) : (
                          <>
                            <Save className="h-3 w-3" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingModel(false);
                          setSelectedModel(agent.defaultModel || 'claude-sonnet-4-20250514');
                        }}
                        disabled={isSavingModel}
                        className="gap-1.5"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setSelectedModel(agent.defaultModel || 'claude-sonnet-4-20250514');
                        setIsEditingModel(true);
                      }}
                      className="gap-1.5"
                    >
                      <Edit className="h-3 w-3" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
              {isEditingModel ? (
                <Select value={selectedModel} onValueChange={setSelectedModel}>
                  <SelectTrigger className="w-full max-w-md">
                    <SelectValue placeholder={isLoadingModels ? "Loading models..." : "Select a model"} />
                  </SelectTrigger>
                  <SelectContent>
                    {isLoadingModels ? (
                      <SelectItem value="_loading" disabled>Loading models...</SelectItem>
                    ) : availableModels.length === 0 ? (
                      <SelectItem value="_none" disabled>No models available</SelectItem>
                    ) : (
                      <>
                        {/* Group models by provider */}
                        {['anthropic', 'openai', 'vertex'].map(provider => {
                          const providerModels = availableModels.filter(m => m.provider === provider);
                          if (providerModels.length === 0) return null;
                          const providerLabel = provider === 'anthropic' ? 'Anthropic' : 
                                               provider === 'openai' ? 'OpenAI' : 'Google Vertex AI';
                          return (
                            <div key={provider}>
                              <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground bg-muted/50">
                                {providerLabel}
                              </div>
                              {providerModels.map((model, index) => (
                                <SelectItem key={`${model.id}-${index}`} value={model.id}>
                                  {model.name} ({model.id})
                                </SelectItem>
                              ))}
                            </div>
                          );
                        })}
                      </>
                    )}
                  </SelectContent>
                </Select>
              ) : (
                <code className="text-sm bg-muted px-2 py-1 rounded">
                  {agent.defaultModel || 'claude-sonnet-4-20250514'}
                </code>
              )}
            </div>

            <Separator />

            {/* Timeout */}
            <div>
              <label className="text-sm font-medium mb-2 block">Timeout</label>
              <span className="text-sm text-muted-foreground">
                {metadata?.timeoutSeconds 
                  ? `${metadata.timeoutSeconds} seconds (${Math.floor(metadata.timeoutSeconds / 60)} minutes)`
                  : '1800 seconds (30 minutes)'}
              </span>
            </div>

            <Separator />

            {/* Agent Config */}
            {metadata?.agentConfig && Object.keys(metadata.agentConfig).length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Agent-Specific Configuration</label>
                <pre className="text-xs bg-muted p-3 rounded overflow-x-auto">
                  {JSON.stringify(metadata.agentConfig, null, 2)}
                </pre>
              </div>
            )}

            <Separator />

            {/* System Prompt Template */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium">System Prompt Template</label>
                <div className="flex gap-2">
                  {isEditingPrompt ? (
                    <>
                      <Button
                        size="sm"
                        onClick={async () => {
                          setIsSavingPrompt(true);
                          try {
                            const result = await updateSystemAgent(agent.id, {
                              systemPromptTemplate: editedPrompt,
                            });
                            if (result.success) {
                              toast({
                                title: 'System prompt updated',
                                description: 'The system prompt template has been saved.',
                              });
                              setIsEditingPrompt(false);
                              // Trigger a refresh of the agent data
                              onRunTriggered?.();
                            } else {
                              toast({
                                title: 'Failed to save',
                                description: result.error || 'An error occurred.',
                                variant: 'destructive',
                              });
                            }
                          } catch (error: any) {
                            toast({
                              title: 'Error',
                              description: error.message || 'Failed to save system prompt.',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsSavingPrompt(false);
                          }
                        }}
                        disabled={isSavingPrompt}
                        className="gap-1.5"
                      >
                        {isSavingPrompt ? (
                          <>
                            <Loader2 className="h-3 w-3 animate-spin" />
                            Saving
                          </>
                        ) : (
                          <>
                            <Save className="h-3 w-3" />
                            Save
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setIsEditingPrompt(false);
                          setEditedPrompt(agent.systemPromptTemplate || '');
                        }}
                        disabled={isSavingPrompt}
                        className="gap-1.5"
                      >
                        <X className="h-3 w-3" />
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setEditedPrompt(agent.systemPromptTemplate || '');
                        setIsEditingPrompt(true);
                      }}
                      className="gap-1.5"
                    >
                      <Edit className="h-3 w-3" />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
              {isEditingPrompt ? (
                <Textarea
                  value={editedPrompt}
                  onChange={(e) => setEditedPrompt(e.target.value)}
                  rows={12}
                  className="font-mono text-xs"
                  placeholder="Enter system prompt template..."
                />
              ) : (
                <div className="max-h-48 overflow-y-auto">
                  <pre className="text-xs bg-muted p-3 rounded whitespace-pre-wrap">
                    {agent.systemPromptTemplate || 'No system prompt configured'}
                  </pre>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Danger Zone */}
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base text-red-600">Danger Zone</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              These actions are irreversible. Please be certain.
            </p>
            <div className="flex gap-2">
              <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">
                <Pause className="h-4 w-4 mr-2" />
                {agent.status === 'active' ? 'Disable Agent' : 'Enable Agent'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  // Render tab content
  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverviewTab();
      case 'findings':
        return renderFindingsTab();
      case 'history':
        return renderHistoryTab();
      case 'schedule':
        return renderScheduleTab();
      case 'configure':
        return renderConfigureTab();
      default:
        return renderOverviewTab();
    }
  };

  return (
    <div className="h-full">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-1.5">
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <div className="flex items-center gap-3">
          <div className="p-1.5 rounded-lg bg-muted">
            {getIcon()}
          </div>
          <div>
            <h1 className="text-xl font-semibold">{agent.displayName || agent.name}</h1>
            <p className="text-sm text-muted-foreground">{getAgentCategoryLabel(agent.category)}</p>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button onClick={handleRunNow} disabled={isRunning || agent.status === 'inactive'} className="gap-2">
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Running...
              </>
            ) : (
              <>
                <Play className="h-4 w-4" />
                Run Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex gap-6 h-[calc(100%-80px)]">
        {/* Tab Menu */}
        <div className="shrink-0 py-2">
          <AgentFocusTabMenu
            activeTab={activeTab}
            onTabChange={setActiveTab}
            findingsCount={openFindingsCount}
          />
        </div>

        {/* Tab Content */}
        <div className="flex-1 min-w-0 overflow-y-auto pb-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
